import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "pwa-install-dismissed";

// App-like install affordance. Android/desktop: native install prompt.
// iOS Safari: shows Add-to-Home-Screen instructions (no native prompt API).
export function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  const isStandalone = () =>
    window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isSafari = isIOS && /safari/.test(ua) && !/crios|fxios/.test(ua);
    if (isSafari) {
      const t = setTimeout(() => setShow(true), 2500);
      setIosHint(true);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) {}
    setDeferred(null);
    dismiss();
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-5 left-5 z-[55] w-[calc(100vw-2.5rem)] sm:w-[320px] bg-card border border-border shadow-2xl p-4"
    >
      <button onClick={dismiss} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground" data-testid="pwa-install-dismiss" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt="Legalis AI" className="w-11 h-11 border border-border shrink-0" />
        <div className="min-w-0">
          <div className="font-serif text-sm font-semibold">Install Legalis AI</div>
          <p className="text-[12px] text-muted-foreground mt-0.5">Add the app to your home screen for a faster, full-screen experience.</p>
        </div>
      </div>
      {iosHint ? (
        <p className="mt-3 text-[12px] text-muted-foreground flex items-center gap-1.5 border-t border-border pt-3">
          Tap <Share className="w-3.5 h-3.5 inline text-brand" /> then <span className="font-medium text-foreground">“Add to Home Screen”</span>.
        </p>
      ) : (
        <button onClick={install} data-testid="pwa-install-btn"
          className="mt-3 w-full h-10 bg-brand text-white text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
          <Download className="w-4 h-4" /> Install app
        </button>
      )}
    </div>
  );
}
